import { cacheKeys } from "@constants/cache-keys";
import { events } from "@constants/tracking";
import { usePracticeTests } from "@features/dashboard/hooks/usePracticeTests";

import { useExamContext } from "@features/exam/contexts/ExamContext";
import { retrieveExamContextFromKV } from "@features/exam/helpers";
import type { IconType } from "@memorang/ui/icon";
import { useQuery } from "@tanstack/react-query";

import { trackCustomEvent } from "analytics";
import {
	applyAccessCode,
	getActiveCodes,
} from "api/commerce/access-code-service";
import { router, useGlobalSearchParams } from "expo-router";
import { useState } from "react";
import type { EXAM_STATUS } from "types/tag";

import { useAuthProvider } from "@helpers/auth-provider";
import { getPlatform } from "@memorang/helpers/platform";
import { handleExternalAuth } from "api/commerce/auth-service";
import {
	fetchExamDetails,
	fetchExamsList,
	updateExamDetails,
} from "api/commerce/exam-service";
import type { ActiveCodesResponse } from "api/commerce/types";
import { getMixedItemDistribution } from "api/queries";
import { useAppStore } from "./useAppStore";
import useAuthUser from "./useAuthUser";

const start_session = "true";
const auto_redeem = "true";

const SESSION_EXISTS_ERROR_CODE = "session_exists";

const handleClerkDeeplinkSignIn = async (
	signInToken: string,
	email: string,
): Promise<string | null> => {
	const { getClerkInstance } = await import("@clerk/clerk-expo");
	const clerk = getClerkInstance();
	try {
		await clerk.session?.remove();
		const signInAttempt = await clerk.client?.signIn.create({
			strategy: "ticket",
			ticket: signInToken,
		});

		if (signInAttempt?.status === "complete") {
			await clerk.setActive({
				session: signInAttempt.createdSessionId,
			});
			return email;
		}
	} catch (error) {
		if (error instanceof Error && "errors" in error) {
			const errorObject = error as { errors: { code: string }[] };
			const code = errorObject.errors[0]?.code;
			if (code === SESSION_EXISTS_ERROR_CODE) {
				return email;
			}
		}
		throw error;
	}
	return null;
};

// Supabase deeplink sign-in path was removed: it called `setSession({ access_token, refresh_token })` with the same token for both fields, which either silently fails refresh or causes the backend to treat a short-lived access token as a long-lived refresh credential.
// TODO(backend): re-enable Supabase deeplink only after `/auth/external` returns separate `access_token` + `refresh_token`.
const handleSupabaseDeeplinkSignIn = (
	_signInToken: string,
	_email: string,
): Promise<string | null> =>
	Promise.reject(
		new Error(
			"Supabase deeplink sign-in is disabled until backend issues separate access/refresh tokens (S-04).",
		),
	);

const findExistingExamContext = async () => {
	const cachedExamContext = await retrieveExamContextFromKV();
	if (cachedExamContext?.examId) {
		return cachedExamContext.examId;
	}

	const latestExams = await fetchExamsList();
	const purchasedExam = latestExams.find((item) =>
		item.exam.bundles.some(
			(bundle) => bundle.metadata?.subscription?.status === "ACTIVE",
		),
	);

	return purchasedExam?.exam.id;
};

const handleActiveCodesRedemption = async (
	activeCodesResult: ActiveCodesResponse[],
	auto_redeem: string,
	source: string,
	setShowAutoRedeemDialog: (show: boolean) => void,
) => {
	if (!activeCodesResult?.length) {
		return null;
	}

	if (auto_redeem !== "true") {
		setShowAutoRedeemDialog(true);
		return null;
	}

	const accessCode = activeCodesResult[0].codes[0];
	trackCustomEvent({
		eventName: events.promoCodeEntered,
		source,
		examId: accessCode,
	});

	const result = await applyAccessCode({ accessCode });

	if (!result.success) {
		return null;
	}

	const examId =
		typeof result.data.examId === "string"
			? result.data.examId
			: result.productData?.[0]?.examId;

	trackCustomEvent({
		eventName: events.promoCodeRedeemed,
		source,
		examId,
		examNumericId: result.data.examNumericId,
		code: accessCode,
	});

	return examId;
};

type LoadingState = {
	key: "accessCode" | "examDetails" | "exams" | "session";
	isLoading: boolean;
};

const getLoadingMessages = (
	states: LoadingState[],
): { text: string; icon: IconType }[] => {
	const messageMap: Record<
		LoadingState["key"],
		{ text: string; icon: IconType }
	> = {
		accessCode: { text: "Activating your access", icon: "lock-open-outline" },
		examDetails: {
			text: "Fetching exam information",
			icon: "clipboard-outline",
		},
		exams: { text: "Loading practice tests", icon: "book-open-outline" },
		session: { text: "Securely logging you in", icon: "shield-check-outline" },
	};

	return states
		.filter((state) => state.isLoading)
		.map((state) => messageMap[state.key]);
};

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
const useDeeplinkHandling = () => {
	const tenantId = useAppStore((state) => state.tenant.id);
	const { provider: authProvider, isClerk } = useAuthProvider();
	const [showAutoRedeemDialog, setShowAutoRedeemDialog] = useState(false);
	const [isAccessRestricted, setIsAccessRestricted] = useState(false);

	const { handleSwitchExamContext } = useExamContext();

	const { userPublicMetadataId, userLogicalId, isLoaded } = useAuthUser();

	const { handlePracticeTestSession, creatingSession } = usePracticeTests();

	const {
		exam_date,
		source,
		auth_token,
		sku: skuFromParams,
	} = useGlobalSearchParams<{
		start_session: string;
		auto_redeem: string;
		exam_date: string;
		source: string;
		auth_token: string;
		sku: string;
	}>();

	const {
		data: email,
		isLoading: isLoadingSession,
		isError: isErrorSession,
	} = useQuery({
		queryKey: ["session", auth_token, authProvider],
		queryFn: async () => {
			const JWT_FORMAT = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
			if (!JWT_FORMAT.test(auth_token)) {
				return null;
			}
			const result = await handleExternalAuth({
				token: auth_token,
				platform: getPlatform(),
				tenantId,
			});
			const signInToken = result?.data.token;
			const resultEmail = result.data.email;

			if (!signInToken) {
				return null;
			}

			if (isClerk) {
				return handleClerkDeeplinkSignIn(signInToken, resultEmail);
			}

			return handleSupabaseDeeplinkSignIn(signInToken, resultEmail);
		},
		staleTime: 0,
		enabled: !!auth_token && isLoaded,
		gcTime: 0,
		retry: false,
	});

	const {
		data: examId,
		isLoading: isLoadingApplyAccessCode,
		isError: isErrorApplyAccessCode,
	} = useQuery({
		queryKey: ["redeem_access_code", tenantId, email, auth_token],
		queryFn: async () => {
			if (!email) {
				return;
			}

			const activeCodesResult = await getActiveCodes({
				sku: skuFromParams,
			});

			const examIdFromRedemption = await handleActiveCodesRedemption(
				activeCodesResult,
				auto_redeem,
				source,
				setShowAutoRedeemDialog,
			);

			if (examIdFromRedemption) {
				return examIdFromRedemption;
			}

			if (skuFromParams) {
				const examDetails = await fetchExamDetails({
					sku: skuFromParams,
					examId: examIdFromRedemption!,
				});
				return examDetails.exam.id;
			}

			const existingExamId = await findExistingExamContext();

			if (!existingExamId) {
				setIsAccessRestricted(true);
				return undefined;
			}

			return existingExamId;
		},
		enabled: !!tenantId && !!email && !!auth_token && !!userLogicalId,
		staleTime: 0,
		gcTime: 0,
		retry: false,
	});

	const { data: examDetails, isLoading: isLoadingExamDetails } = useQuery({
		queryKey: [cacheKeys.examDetails, examId, auth_token],
		queryFn: async () => {
			if (!examId) {
				return;
			}
			const result = await fetchExamDetails({
				examId,
			});
			await handleSwitchExamContext({
				...result,
				...(exam_date
					? {
							metadata: {
								examDate: exam_date,
								isActiveExam: true,
							},
						}
					: {}),
			});
			if (exam_date) {
				updateExamDetails({
					examId,
					metadata: {
						examDate: exam_date,
					},
				});
			}
			return result;
		},
		enabled: !!examId && !!auth_token,
		staleTime: 0,
		gcTime: 0,
	});

	const { isLoading: isLoadingExams, isSuccess: isExamsSuccess } = useQuery({
		queryKey: [cacheKeys.exams, examId, tenantId, auth_token],
		queryFn: async () => {
			if (!(examDetails && userPublicMetadataId)) {
				return;
			}

			const result = await getMixedItemDistribution({
				userPublicMetadataId,
				action: "GET_PRATICETEST_DISTRIBUTION",
				examId: examId!,
			});
			const [firstExam] =
				result.exams?.filter(
					(exam) => exam.subType === "PRACTICE_TEST" && exam.hasAccess,
				) || [];

			if (!firstExam) {
				setIsAccessRestricted(true);
				return;
			}
			if (start_session === "true") {
				await handlePracticeTestSession(
					firstExam.id,
					firstExam.status as EXAM_STATUS,
					firstExam.numericId!,
					false,
					true,
					false,
					firstExam.collectionId,
					true,
				);
			} else {
				router.push("/practice-tests");
			}
			return result;
		},
		enabled: !!examDetails && !!auth_token,
		staleTime: 0,
		gcTime: 0,
	});

	const isDeeplinkFlowActive = !!auth_token;

	const isWaitingForAccessCodeQuery =
		!!email && !examId && !isAccessRestricted && !isErrorApplyAccessCode;
	const isWaitingForExamDetailsQuery = !!examId && !examDetails;
	const isWaitingForExamsQuery = !!examDetails && !isExamsSuccess;

	const loadingStates: LoadingState[] = [
		{
			key: "session",
			isLoading:
				isLoadingSession || (isDeeplinkFlowActive && !email && !isErrorSession),
		},
		{
			key: "accessCode",
			isLoading: isLoadingApplyAccessCode || isWaitingForAccessCodeQuery,
		},
		{
			key: "examDetails",
			isLoading: isLoadingExamDetails || isWaitingForExamDetailsQuery,
		},
		{
			key: "exams",
			isLoading: isLoadingExams || isWaitingForExamsQuery || creatingSession,
		},
	];

	const messages = getLoadingMessages(loadingStates);

	const isLoading =
		isDeeplinkFlowActive &&
		(isLoadingApplyAccessCode ||
			isLoadingExamDetails ||
			isLoadingExams ||
			isLoadingSession ||
			isWaitingForAccessCodeQuery ||
			isWaitingForExamDetailsQuery ||
			isWaitingForExamsQuery ||
			creatingSession);

	const showError = auth_token && (isErrorApplyAccessCode || isErrorSession);
	return {
		creatingSession,
		showAutoRedeemDialog,
		isLoading,
		messages,
		showError,
		email,
		isAccessRestricted,
		setIsAccessRestricted,
	};
};

export default useDeeplinkHandling;
